import { useEffect, type ReactNode } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@hostfully/ui-components';
import LoginFormErrorMessageRenderer from './LoginFormErrorMessageRenderer';
import {
  InputContainer,
  LinkContainer,
  SubmitButtonContainer,
} from './Login.styles';
import LoginSignUpLink from './LoginSignUpLink';
import useLoginUrlContext from './useLoginUrlContext';
import { APP_URL_PARAM } from './Login.constants';
import CheckboxField from '@/components/forms/CheckboxField';
import FormFieldset from '@/components/forms/form/FormFieldset.tsx';
import InputField from '@/components/forms/InputField';
import PasswordField from '@/components/forms/PasswordField';
import useIsMobileScreen from '@/hooks/useIsMobileScreen';

const LoginForm = ({
  noticeMessage,
  clearErrors,
  isDesktopServiceProviderUser = false,
  isSubmitting,
}: {
  noticeMessage: ReactNode;
  clearErrors: () => void;
  isDesktopServiceProviderUser: boolean;
  isSubmitting: boolean;
}) => {
  const { t } = useTranslation();
  const { clearErrors: clearFieldErrors, getFieldState } = useFormContext();
  const { errors } = useFormState();
  const { email, password, registrationToken } = useWatch();
  const isMobile = useIsMobileScreen();
  const { appContext } = useLoginUrlContext();
  const forgotPasswordTo = appContext.appUrl
    ? `/forgot-password?${APP_URL_PARAM}=${encodeURIComponent(appContext.appUrl)}`
    : '/forgot-password';

  // Editing either credential clears the backend error
  useEffect(() => {
    clearErrors();
    if (getFieldState('email').error?.type === 'manual') {
      clearFieldErrors('email');
    }
    if (getFieldState('password').error?.type === 'manual') {
      clearFieldErrors('password');
    }
  }, [email, password, clearErrors, getFieldState, clearFieldErrors]);

  const hasCredentialError =
    errors.email?.type === 'manual' || errors.password?.type === 'manual';

  return (
    <FormFieldset disabled={isSubmitting}>
      <InputContainer>
        <InputField
          placeholder={t('pageLogin.emailAddress')}
          label={t('pageLogin.emailAddress')}
          name="email"
          type="email"
          disabled={!!registrationToken}
        />
      </InputContainer>
      <InputContainer>
        <PasswordField
          placeholder={t('pageLogin.password')}
          label={t('pageLogin.password')}
          name="password"
        />
      </InputContainer>
      <InputContainer>
        <CheckboxField name="rememberMe" label={t('pageLogin.rememberMe')} />
      </InputContainer>
      <LinkContainer>
        <LoginSignUpLink url={import.meta.env.VITE_SIGNUP_PAGE_URL} />
        <Link
          className="action-link"
          data-testid="forgot-password-link"
          to={forgotPasswordTo}
        >
          {t('pageLogin.forgotPassword')}
        </Link>
      </LinkContainer>
      <SubmitButtonContainer>
        <Button
          data-testid="submit-button"
          disabled={
            hasCredentialError ||
            !!noticeMessage ||
            isDesktopServiceProviderUser
          }
          type="submit"
          size="large"
          fullWidth={isMobile}
        >
          {t('pageLogin.signIn')}
        </Button>
      </SubmitButtonContainer>
      <LoginFormErrorMessageRenderer noticeMessage={noticeMessage} />
    </FormFieldset>
  );
};

export default LoginForm;
